import { useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import ActivityParticipant from '../ActivityParticipant';
import Select from 'components/general/ViewportSelect';

const userId = (user) => String(user?.tmAuthUserId || user?.userId || user?.id || user?._id || '');

const debateSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 40,
    borderRadius: 8,
    borderColor: state.isFocused ? 'var(--mypathway-accent, var(--tm-yellow, #f2df74))' : 'var(--pathway-card-border, rgba(255, 255, 255, 0.25))',
    backgroundColor: 'var(--pathway-chip-bg, rgba(255, 255, 255, 0.08))',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(242, 223, 116, 0.18)' : 'none',
    '&:hover': {
      borderColor: 'var(--mypathway-accent, var(--tm-yellow, #f2df74))',
    },
  }),
  menu: (base) => ({
    ...base,
    zIndex: 2050,
    border: '1px solid var(--pathway-card-border, rgba(255, 255, 255, 0.25))',
    backgroundColor: 'var(--background, #fff)',
  }),
  menuList: (base) => ({
    ...base,
    backgroundColor: 'var(--background, #fff)',
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused ? 'var(--tm-yellow-transparent, rgba(242, 223, 116, 0.18))' : 'transparent',
    color: 'var(--foreground, #222)',
    cursor: 'pointer',
  }),
  singleValue: (base) => ({ ...base, color: 'var(--foreground, #222)' }),
  input: (base) => ({ ...base, color: 'var(--foreground, #222)' }),
  placeholder: (base) => ({ ...base, color: 'var(--pathway-muted-text, rgba(255, 255, 255, 0.7))' }),
  indicatorSeparator: (base) => ({ ...base, backgroundColor: 'var(--pathway-card-border, rgba(255, 255, 255, 0.25))' }),
};

export default function DebateAssignments({ item, users, meeting, getMeeting, canManage, collapsible = false, summaryOnly = false, expanded: controlledExpanded, onToggle }) {
  const { user: authUser } = useSelector(state => state.auth);
  const [localExpanded, setLocalExpanded] = useState(!collapsible);
  const expanded = controlledExpanded ?? localExpanded;
  const setExpanded = onToggle ? () => onToggle() : setLocalExpanded;
  const [sides, setSides] = useState({ pro: [], con: [] });
  const [count, setCount] = useState(1);
  const [members, setMembers] = useState([]);
  const [membersError, setMembersError] = useState('');
  const hasProvidedUsers = Array.isArray(users) && users.length > 0;
  const availableUsers = hasProvidedUsers ? users : members;
  useEffect(() => {
    if (summaryOnly || hasProvidedUsers || !authUser?.token) return undefined;
    const controller = new AbortController();
    (async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_URL}/users`, {
          headers: { Authorization: `Bearer ${authUser.token}` }, signal: controller.signal,
        });
        if (!response.ok) throw new Error('Unable to load debate participants.');
        const data = await response.json();
        if (!Array.isArray(data)) throw new Error('Invalid debate participants response.');
        if (!controller.signal.aborted) setMembers(data);
      } catch (error) {
        if (!controller.signal.aborted) setMembersError(error.message);
      }
    })();
    return () => controller.abort();
  }, [summaryOnly, hasProvidedUsers, authUser?.token]);
  useEffect(() => {
    const slotCount = item.activity?.debaterCount || Math.min(3, Math.max(1,
      item.activity?.sides?.pro?.length || 0, item.activity?.sides?.con?.length || 0));
    setCount(slotCount);
    setSides({
      pro: Array.from({ length: slotCount }, (_, index) => userId(item.activity?.sides?.pro?.[index])),
      con: Array.from({ length: slotCount }, (_, index) => userId(item.activity?.sides?.con?.[index])),
    });
  }, [item.activity]);
  const disabled = !canManage || meeting?.meetingDetails?.isLocked;
  const save = async (nextSides, nextCount) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_URL}/agendaItem/${item.agendaItemId}/activity/assignments`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authUser?.token || ''}` },
        body: JSON.stringify({ sides: nextSides, debaterCount: nextCount }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Unable to save debate assignments.');
      await getMeeting();
    } catch (error) {
      toast.error(error.message, { position: 'bottom-left' });
    }
  };
  const heading = <div className="debate-assignments-heading">
    {collapsible
      ? <button type="button" className="debate-assignments-kicker debate-assignments-toggle"
        aria-expanded={expanded} onClick={() => setExpanded(value => !value)}>
        <span aria-hidden="true">{expanded ? '▾' : '▸'}</span> Assignments
      </button>
      : <span className="debate-assignments-kicker">Assignments</span>}
  </div>;
  const summary = <div className="debate-assignments-summary" role="group" aria-label="Assigned debaters">
    {['pro', 'con'].map(side => <div key={side} className={`debate-assignments-summary-side debate-assignments-side-${side}`}>
      {(item.activity?.sides?.[side] || []).filter(Boolean).map((user, index) =>
        <div key={userId(user) || index} className="debate-assignments-summary-debater">
          <span className={`debate-side-badge debate-side-badge-${side}`}>{side.toUpperCase()}</span>
          <ActivityParticipant user={user} meeting={meeting} />
        </div>)}
    </div>)}
  </div>;
  // Agenda card mode: header only when collapsed, assigned people only when expanded (editing stays in the modal).
  if (summaryOnly) {
    return <section className={`debate-assignments p-3 mb-3${expanded ? '' : ' is-collapsed'}`}>
      {heading}
      {expanded && summary}
    </section>;
  }
  if (collapsible && !expanded) {
    return <section className="debate-assignments p-3 mb-3">
      {heading}
      {summary}
    </section>;
  }
  return <section className="debate-assignments p-3 mb-3">
    {heading}
    {membersError && <div role="alert" className="text-danger">{membersError}</div>}
    <div className="debate-assignments-count-row">
      <Form.Label className="debate-assignments-label" htmlFor={`debater-count-${item.agendaItemId}`}>
        Debaters per side
      </Form.Label>
      <Select inputId={`debater-count-${item.agendaItemId}`} aria-label="Debaters per side"
        value={{ value: String(count), label: String(count) }}
        options={[1, 2, 3].map(value => ({ value: String(value), label: String(value) }))}
        isSearchable={false} isClearable={false} isDisabled={disabled}
        className="debate-assignments-count" styles={debateSelectStyles}
        onChange={option => {
          const next = Number(option?.value);
          if (next < count && ['pro', 'con'].some(side => sides[side].slice(next).some(Boolean))
            && !window.confirm('Remove the assignments in the extra debate slots?')) return;
          const nextSides = Object.fromEntries(['pro', 'con'].map(side => [
            side, Array.from({ length: next }, (_, index) => sides[side][index] || ''),
          ]));
          setCount(next);
          setSides(nextSides);
          save(nextSides, next);
        }} />
    </div>
    <div className="debate-assignments-sides">
      {['pro', 'con'].map(side => <fieldset key={side} className={`debate-assignments-side debate-assignments-side-${side}`} disabled={disabled}>
        <legend>{side === 'pro' ? 'Pro' : 'Con'}</legend>
        {Array.from({ length: count }, (_, index) => {
          const assigned = item.activity?.sides?.[side]?.[index];
          const candidates = [...availableUsers, ...(item.activity?.sides?.pro || []), ...(item.activity?.sides?.con || [])].filter(Boolean);
          const options = Array.from(new Map(candidates.map(user => [userId(user), {
            value: userId(user), label: `${user.firstName || ''} ${user.lastName || ''}`.trim(),
          }])).values());
          const selected = sides[side][index] || '';
          return <div key={index} className="debate-assignments-slot">
            <Form.Label htmlFor={`debate-${item.agendaItemId}-${side}-${index}`}>{side === 'pro' ? 'Pro' : 'Con'} {index + 1}</Form.Label>
            <Select inputId={`debate-${item.agendaItemId}-${side}-${index}`}
              aria-label={`${side === 'pro' ? 'Pro' : 'Con'} ${index + 1}`}
              isSearchable isClearable isDisabled={disabled}
              options={options.filter(option => option.value === selected
                || ![...sides.pro, ...sides.con].includes(option.value))}
              value={options.find(option => option.value === selected) || null}
              placeholder="Choose debater"
              styles={debateSelectStyles}
              onChange={option => {
                const next = { ...sides, [side]: sides[side].map((id, slot) => slot === index ? option?.value || '' : id) };
                setSides(next);
                save(next, count);
              }} />
            {!canManage && assigned && <ActivityParticipant user={assigned} meeting={meeting} />}
          </div>;
        })}
      </fieldset>)}
    </div>
  </section>;
}
